(() => {
  if (window.__cornerstoneCustomContentTogglesInitialised) return;
  window.__cornerstoneCustomContentTogglesInitialised = true;

  const observedContent = new WeakSet();
  const observedSliders = new WeakSet();
  const sliderUpdates = new WeakMap();
  let resizeFrame;

  const getContent = (button) => {
    const contentId = button.getAttribute('aria-controls');
    return contentId ? document.getElementById(contentId) : null;
  };

  const updateButtonVisibility = (button) => {
    const content = getContent(button);
    if (!content) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      button.hidden = false;
      return;
    }

    requestAnimationFrame(() => {
      button.hidden = content.scrollHeight <= content.clientHeight + 1;
    });
  };

  const initialiseButton = (button) => {
    updateButtonVisibility(button);

    const content = getContent(button);
    if (content && !observedContent.has(content) && 'ResizeObserver' in window) {
      const observer = new ResizeObserver(() => updateButtonVisibility(button));
      observer.observe(content);
      observedContent.add(content);
    }
  };

  const initialise = (root = document) => {
    const buttons = [
      ...(root.matches?.('[data-media-text-toggle]') ? [root] : []),
      ...root.querySelectorAll('[data-media-text-toggle]'),
    ];

    buttons.forEach(initialiseButton);

    const sliderContainers = [
      ...(root.matches?.('.minimal-collection-list__slider') ? [root] : []),
      ...root.querySelectorAll('.minimal-collection-list__slider'),
    ];

    sliderContainers.forEach(initialiseMinimalSlider);
  };

  function initialiseMinimalSlider(container) {
    const viewport = container.querySelector('.scroll-slider__slider');
    if (!viewport) return;

    if (sliderUpdates.has(viewport)) {
      sliderUpdates.get(viewport)();
      return;
    }

    const previousButton = container.querySelector('[data-scroll-slider-prev-button]');
    const nextButton = container.querySelector('[data-scroll-slider-next-button]');

    const update = () => {
      requestAnimationFrame(() => {
        const contentRequiresNavigation = viewport.scrollWidth > viewport.clientWidth + 1;
        const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const currentScroll = Math.max(0, viewport.scrollLeft);
        const canScrollPrevious = contentRequiresNavigation && maximumScroll > 1 && currentScroll > 1;
        const canScrollNext = contentRequiresNavigation && maximumScroll > 1 && currentScroll < maximumScroll - 1;

        container.dataset.canScrollPrev = String(canScrollPrevious);
        container.dataset.canScrollNext = String(canScrollNext);

        if (previousButton) {
          previousButton.disabled = !canScrollPrevious;
          previousButton.classList.toggle('hidden', !canScrollPrevious);
        }

        if (nextButton) {
          nextButton.disabled = !canScrollNext;
          nextButton.classList.toggle('hidden', !canScrollNext);
        }
      });
    };

    sliderUpdates.set(viewport, update);
    update();

    if (!observedSliders.has(viewport)) {
      viewport.addEventListener('scroll', update, { passive: true });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update);
        observer.observe(viewport);
        viewport.querySelectorAll(':scope > *').forEach((item) => observer.observe(item));
      }

      observedSliders.add(viewport);
    }
  }

  document.addEventListener('click', (event) => {
    const navigationButton = event.target.closest(
      '[data-scroll-slider-prev-button], [data-scroll-slider-next-button]'
    );
    if (!navigationButton || navigationButton.disabled) return;

    const container = navigationButton.closest('.minimal-collection-list__slider');
    const minimalList = navigationButton.closest('.minimal-collection-list');
    if (!container || minimalList?.dataset.fitButtonsToText !== 'true') return;

    const viewport = container.querySelector('.scroll-slider__slider');
    if (!viewport) return;

    const items = [...viewport.querySelectorAll(':scope > .minimal-collection-list__button')];
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const viewportBounds = viewport.getBoundingClientRect();
    const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const isNext = navigationButton.matches('[data-scroll-slider-next-button]');
    const pageDistance = viewport.clientWidth;
    const itemPositions = items.map((item) => (
      viewport.scrollLeft + item.getBoundingClientRect().left - viewportBounds.left
    ));
    let destination = viewport.scrollLeft;

    if (isNext) {
      const pageTarget = viewport.scrollLeft + pageDistance;
      destination = itemPositions.find((position) => position >= pageTarget - 1) ?? maximumScroll;
    } else {
      const pageTarget = viewport.scrollLeft - pageDistance;
      destination = [...itemPositions]
        .reverse()
        .find((position) => position <= pageTarget + 1) ?? 0;
    }

    viewport.scrollTo({
      left: Math.max(0, Math.min(maximumScroll, destination)),
      behavior: 'smooth',
    });
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-media-text-toggle]');
    if (!button) return;

    const content = getContent(button);
    if (!content) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    const nextExpanded = !expanded;

    button.setAttribute('aria-expanded', String(nextExpanded));
    content.dataset.expanded = String(nextExpanded);
    button.textContent = nextExpanded ? button.dataset.readLessLabel : button.dataset.readMoreLabel;

    if (!nextExpanded) {
      updateButtonVisibility(button);
    }
  });

  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => initialise());
  });

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          initialise(node);
          const parentSlider = node.parentElement?.closest('.minimal-collection-list__slider');
          if (parentSlider) initialiseMinimalSlider(parentSlider);
        }
      });
    });
  });

  const start = () => {
    initialise();
    document.fonts?.ready?.then(() => initialise());
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
